import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Badge,
  Form,
  InputGroup,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { authFetch } from "../utils/authFetch";

const toYMD = (d) => format(d, "yyyy-MM-dd");

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

function SortHeader({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "↕";
  return (
    <th
      role="button"
      className="user-select-none"
      onClick={() => onSort(col)}
      title={`Sort by ${label}`}
    >
      <span className="me-1">{label}</span>
      <span style={{ opacity: 0.6, fontSize: 12 }}>{arrow}</span>
    </th>
  );
}

export function CashDocList() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountId = searchParams.get("account_id") || "";

  // filtre
  const [searchClient, setSearchClient] = useState("");
  const [searchDocNo, setSearchDocNo] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterRecType, setFilterRecType] = useState(""); // "" = Toate, "20" = Încasări, "21" = Plăți
  const [filterAccountId, setFilterAccountId] = useState(urlAccountId);
  const [cashAccounts, setCashAccounts] = useState([]);

  // sort
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  // paginate
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const onSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sigur doriți să ștergeți această înregistrare?")) {
      return;
    }
    try {
      await authFetch(`/cash-doc/${id}`, {
        method: "DELETE",
      });
      fetchList();
    } catch (e) {
      alert(`Eroare la ștergere: ${e.message || e}`);
    }
  };

  // Load cash accounts (51, 53)
  useEffect(() => {
    (async () => {
      try {
        const acc = await authFetch("/accounts/cash-menu");
        setCashAccounts(Array.isArray(acc) ? acc : []);
      } catch (e) {
        console.error("Nu s-au putut încărca conturile de casă/bancă:", e);
      }
    })();
  }, []);

  // Sync local filter state with URL param changes (e.g. navigation clicks)
  useEffect(() => {
    setFilterAccountId(urlAccountId);
  }, [urlAccountId]);

  // Reset pagina la schimbarea filtrelor/sortării/mărimii paginii
  useEffect(() => {
    setCurrentPage(0);
  }, [
    searchClient,
    searchDocNo,
    startDate,
    endDate,
    filterRecType,
    filterAccountId,
    sortBy,
    sortDir,
    itemsPerPage,
  ]);

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      let start = startDate,
        end = endDate;
      if (start && !end) end = start;
      if (end && !start) start = end;

      const skip = currentPage * itemsPerPage;

      const usp = new URLSearchParams();
      if (searchClient?.trim()) usp.set("client", searchClient.trim());
      if (searchDocNo?.trim()) usp.set("doc_no", searchDocNo.trim());
      if (start instanceof Date) usp.set("date_from", toYMD(start));
      if (end instanceof Date) usp.set("date_to", toYMD(end));
      if (filterRecType) usp.set("rec_type", filterRecType);
      if (filterAccountId) usp.set("cash_account_id", filterAccountId);

      usp.set("sort_by", sortBy);
      usp.set("sort_dir", sortDir);
      usp.set("skip", String(skip));
      usp.set("limit", String(itemsPerPage));

      const path = `/cash-doc/${usp.toString() ? `?${usp.toString()}` : ""}`;
      const data = await authFetch(path);

      if (data && Array.isArray(data.items) && typeof data.total === "number") {
        setItems(data.items);
        setTotalCount(data.total);
      } else if (Array.isArray(data)) {
        setItems(data);
        setTotalCount(data.length);
      } else {
        setItems([]);
        setTotalCount(0);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    itemsPerPage,
    searchClient,
    searchDocNo,
    startDate,
    endDate,
    filterRecType,
    filterAccountId,
    sortBy,
    sortDir,
  ]);

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const from = totalCount ? currentPage * itemsPerPage + 1 : 0;
  const to = Math.min(totalCount, (currentPage + 1) * itemsPerPage);

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h3 className="mb-0">Registru de Casă / Bancă (Plăți & Încasări)</h3>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            as={Link}
            to={`/cash/new?rec_type=20${filterAccountId ? `&account_id=${filterAccountId}` : ""}`}
            variant="success"
          >
            + Încasări
          </Button>
          <Button
            as={Link}
            to={`/cash/new?rec_type=21${filterAccountId ? `&account_id=${filterAccountId}` : ""}`}
            variant="danger"
          >
            + Plăți
          </Button>
        </Col>
      </Row>

      <Row className="mb-3 g-2">
        <Col md={2}>
          <Form.Control
            type="text"
            placeholder="Caută client/partener"
            value={searchClient}
            onChange={(e) => setSearchClient(e.target.value)}
          />
        </Col>
        <Col md={1}>
          <Form.Control
            type="text"
            placeholder="Nr. doc"
            value={searchDocNo}
            onChange={(e) => setSearchDocNo(e.target.value)}
          />
        </Col>
        <Col md={1}>
          <Form.Select
            value={filterRecType}
            onChange={(e) => setFilterRecType(e.target.value)}
          >
            <option value="">Toate</option>
            <option value="20">Încasări</option>
            <option value="21">Plăți</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filterAccountId}
            onChange={(e) => {
              const val = e.target.value;
              setFilterAccountId(val);
              if (val) {
                setSearchParams({ account_id: val });
              } else {
                setSearchParams({});
              }
            }}
          >
            <option value="">Toate conturile</option>
            {cashAccounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <InputGroup>
            <InputGroup.Text>De la</InputGroup.Text>
            <DatePicker
              selected={startDate}
              onChange={setStartDate}
              dateFormat="dd.MM.yyyy"
              wrapperClassName="dp-w120"
              className="form-control"
              placeholderText="Start"
              isClearable
              selectsStart
              startDate={startDate}
              endDate={endDate}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <InputGroup>
            <InputGroup.Text>Până la</InputGroup.Text>
            <DatePicker
              selected={endDate}
              onChange={setEndDate}
              dateFormat="dd.MM.yyyy"
              wrapperClassName="dp-w120"
              className="form-control"
              placeholderText="End"
              isClearable
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate || undefined}
            />
          </InputGroup>
        </Col>

        <Col
          md={2}
          className="d-flex justify-content-end align-items-center gap-1"
        >
          <Form.Select
            size="sm"
            style={{ width: 85 }}
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            title="Rânduri pe pagină"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}/pag.
              </option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" onClick={fetchList} size="sm">
            Caută
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSearchClient("");
              setSearchDocNo("");
              setFilterRecType("");
              setStartDate(null);
              setEndDate(null);
              setFilterAccountId("");
              setSearchParams({});
            }}
            size="sm"
          >
            Clear
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          {totalCount ? `Afișez ${from}-${to} din ${totalCount}` : "—"}
        </small>
        <small className="text-muted">
          Sort: {sortBy} {sortDir}
        </small>
      </div>

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <SortHeader
                  label="ID"
                  col="id"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <th>Tip</th>
                <SortHeader
                  label="Doc No"
                  col="doc_no"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Data Doc"
                  col="doc_date"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Contact / Partener"
                  col="contact"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <th>Cont Casă/Bancă</th>
                <SortHeader
                  label="Suma"
                  col="total"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <th>Explicații</th>
                <th>Status</th>
                <th className="text-end">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.id}</td>
                  <td>
                    {it.rec_type === 20 ? (
                      <Badge bg="success">Încasare</Badge>
                    ) : (
                      <Badge bg="danger">Plată</Badge>
                    )}
                  </td>
                  <td>{it.doc_no || "-"}</td>
                  <td>{formatDateRo(it.doc_date) || "-"}</td>
                  <td>{it.contact_name ?? it.contact_id}</td>
                  <td>{it.cash_account_name || it.cash_account_id}</td>
                  <td>{it.total ? Number(it.total).toFixed(2) : "0.00"}</td>
                  <td>{it.explic || "-"}</td>
                  <td>
                    {it.is_closed ? (
                      <Badge bg="secondary">Închis</Badge>
                    ) : (
                      <Badge bg="primary">Deschis</Badge>
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      as={Link}
                      to={`/cash/${it.id}`}
                      size="sm"
                      variant="outline-primary"
                    >
                      Editează
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="ms-2"
                      disabled={it.is_closed}
                      onClick={() => handleDelete(it.id)}
                    >
                      Șterge
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-4">
                    Nu există înregistrări.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <ReactPaginate
            previousLabel={"← Înapoi"}
            nextLabel={"Înainte →"}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={(event) => setCurrentPage(event.selected)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            renderOnZeroPageCount={null}
          />
        </>
      )}
    </Container>
  );
}
